<script setup lang="ts">
import * as signalR from '@microsoft/signalr';
import { onMounted } from 'vue';

import SoftoolsGlobalStore from '../../Store/softools-global-store';
import type { FeedbackNotification } from './feeback-notification';
import { appStudioToastService } from '../app-studio-toast';

// As recommended in https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-7.0&tabs=visual-studio
// Use our own retry loop to start the SignalR connection, because that behaviour isn't built in to the client.
async function startSignalRConnection(connection: signalR.HubConnection): Promise<void> {
    try {
        await connection.start();
        window.softools.signalRConnection = connection;
        window.softools.signalRConnectionId = connection.connectionId as string;
        SoftoolsGlobalStore.getSoftoolsSignalrModule().setSignalRIsConnected(true);
    } catch (err) {
        console.log(`SignalR connection failed to start: ${err}`);
        setTimeout(() => startSignalRConnection(connection), 5000);
    }
}

onMounted(() => {
    const connection = new signalR.HubConnectionBuilder()
        .withUrl('/softoolssignalrhub', {
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds(retryContext: signalR.RetryContext): number | null {
                // Retry indefinitely after 1 second, then 2, 3, 4, 5, 5, 5, ...
                return Math.min(retryContext.elapsedMilliseconds + 1000, 5000);
            }
        })
        // Connection failures are handled and retried below. Disable the SignalR
        // console logger so transient transport errors are not captured by Sentry.
        .configureLogging(signalR.LogLevel.None)
        .build();

    connection.on('ReceiveNotificationMessage', (notification: FeedbackNotification) => {
        if (notification && notification.type && notification.title && notification.message) {
            appStudioToastService.show(notification.message, {
                title: notification.title,
                variant: notification.type,
                toaster: 'sof-notification-toaster',
                autoHideDelay: 5000,
                solid: true,
                href: notification.href
            });
        }
    });

    connection.onclose((err) => {
        console.log(`SignalR connection closed: ${err}`);
        SoftoolsGlobalStore.getSoftoolsSignalrModule().setSignalRIsConnected(false);

        // Retry manually a while later.
        setTimeout(() => startSignalRConnection(connection), 30000);
    });

    connection.onreconnecting(() => {
        SoftoolsGlobalStore.getSoftoolsSignalrModule().setSignalRIsConnected(false);
    });

    connection.onreconnected(() => {
        SoftoolsGlobalStore.getSoftoolsSignalrModule().setSignalRIsConnected(true);
    });

    void startSignalRConnection(connection);
});
</script>

<template>
    <div class="b-toaster-top-right sof-b-toaster-top-right" data-toaster="sof-notification-toaster" />
</template>
